import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BarChart3, ExternalLink, Loader2, Mail, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import {
  deleteConciergeSite,
  getConciergeSite,
  setConciergeStatus,
  updateConciergeSite,
  type ConciergeStatus,
} from "@/lib/admin/concierge-api";
import { StatusBadge } from "@/components/admin/concierge/StatusBadge";
import { PublishConfirmDialog } from "@/components/admin/concierge/PublishConfirmDialog";
import { ResourcesPanel } from "@/components/admin/concierge/ResourcesPanel";
import { CoachPicker } from "@/components/admin/concierge/CoachPicker";
import { AgentPicker } from "@/components/admin/concierge/AgentPicker";
import { InviteByEmailDialog } from "@/components/admin/concierge/InviteByEmailDialog";

export default function ConciergeEditor() {
  const { siteId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: site, isLoading } = useQuery({
    queryKey: ["concierge", "admin", "site", siteId],
    queryFn: () => getConciergeSite(siteId),
    enabled: !!siteId,
  });

  const [form, setForm] = useState({
    title: "",
    advisor_name: "",
    elevenlabs_agent_id: "",
    coach_user_id: null as string | null,
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  useEffect(() => {
    if (site) {
      setForm({
        title: site.title ?? "",
        advisor_name: site.advisor_name ?? "",
        elevenlabs_agent_id: site.elevenlabs_agent_id ?? "",
        coach_user_id: site.coach_user_id ?? null,
      });
    }
  }, [site]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["concierge", "admin", "site", siteId] });
    if (site) qc.invalidateQueries({ queryKey: ["concierge", "admin", "sites", site.org_id] });
  };

  const saveM = useMutation({
    mutationFn: () =>
      updateConciergeSite(siteId, {
        title: form.title || null,
        advisor_name: form.advisor_name || null,
        elevenlabs_agent_id: form.elevenlabs_agent_id.trim() || null,
        coach_user_id: form.coach_user_id,
      }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const statusM = useMutation({
    mutationFn: (target: ConciergeStatus) => setConciergeStatus(siteId, target),
    onSuccess: invalidate,
    onError: (e: any) => toast({ title: "Status change failed", description: e.message, variant: "destructive" }),
  });

  const deleteM = useMutation({
    mutationFn: () => deleteConciergeSite(siteId),
    onSuccess: () => {
      if (site) qc.invalidateQueries({ queryKey: ["concierge", "admin", "sites", site.org_id] });
      navigate("/app/admin/concierge");
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });

  const [confirm, setConfirm] = useState<ConciergeStatus | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin opacity-60" />
      </div>
    );
  }
  if (!site) {
    return <p className="text-sm text-muted-foreground">Site not found.</p>;
  }

  const dirty =
    (form.title ?? "") !== (site.title ?? "") ||
    (form.advisor_name ?? "") !== (site.advisor_name ?? "") ||
    (form.elevenlabs_agent_id ?? "") !== (site.elevenlabs_agent_id ?? "") ||
    (form.coach_user_id ?? null) !== (site.coach_user_id ?? null);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button asChild variant="ghost" size="icon">
            <Link to="/app/admin/concierge" aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-heading font-semibold truncate">
                {site.title || "Untitled"}
              </h1>
              <StatusBadge status={site.status} />
            </div>
            <p className="text-xs text-muted-foreground">
              Only visible to the target user once published.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to={`/app/admin/concierge/${site.id}/insights`}>
              <BarChart3 className="h-4 w-4 mr-1" /> Engagement
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href={`/app/concierge?siteId=${site.id}`} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" /> Preview
            </a>
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Advisor name</Label>
            <Input
              placeholder="Charles"
              value={form.advisor_name}
              onChange={(e) => setForm((s) => ({ ...s, advisor_name: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Voice agent</Label>
            <AgentPicker
              orgId={site.org_id}
              value={form.elevenlabs_agent_id || null}
              onChange={(v) => setForm((s) => ({ ...s, elevenlabs_agent_id: v ?? "" }))}
            />
            <p className="text-xs text-muted-foreground">
              "Use default pinned agent" preserves the platform default. Add or edit agents via Manage.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Coach</Label>
            <CoachPicker
              orgId={site.org_id}
              value={form.coach_user_id}
              onChange={(v) => setForm((s) => ({ ...s, coach_user_id: v }))}
            />
            <p className="text-xs text-muted-foreground">
              The named coach for this space. They can see engagement — but not conversation content.
            </p>
          </div>
          <div className="flex justify-between items-center">
            <Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
              <Mail className="h-4 w-4 mr-1" /> Invite by email
            </Button>
            <Button disabled={!dirty || saveM.isPending} onClick={() => saveM.mutate()}>
              {saveM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <h2 className="text-lg font-heading font-semibold">Publish</h2>
            <p className="text-sm text-muted-foreground">
              Current status: <StatusBadge status={site.status} />
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {site.status !== "published" && (
              <Button onClick={() => setConfirm("published")} disabled={statusM.isPending}>
                Publish
              </Button>
            )}
            {site.status === "published" && (
              <Button variant="outline" onClick={() => setConfirm("draft")} disabled={statusM.isPending}>
                Unpublish (back to draft)
              </Button>
            )}
            {site.status !== "archived" && (
              <Button variant="outline" onClick={() => setConfirm("archived")} disabled={statusM.isPending}>
                Archive
              </Button>
            )}
            {site.status === "archived" && (
              <Button variant="outline" onClick={() => statusM.mutate("draft")} disabled={statusM.isPending}>
                Restore to draft
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <ResourcesPanel siteId={site.id} orgId={site.org_id} />

      <Card>
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <h2 className="text-base font-medium">Delete site</h2>
            <p className="text-sm text-muted-foreground">Removes the site and all its resources.</p>
          </div>
          <Button
            variant="outline"
            className="text-destructive border-destructive/40 hover:bg-destructive/10"
            onClick={() => {
              if (confirm !== null) return;
              if (window.confirm("Delete this Concierge site and all its resources?")) {
                deleteM.mutate();
              }
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </CardContent>
      </Card>

      <PublishConfirmDialog
        open={confirm !== null}
        onOpenChange={(v) => !v && setConfirm(null)}
        target={confirm ?? "published"}
        onConfirm={() => {
          if (confirm) {
            statusM.mutate(confirm);
            setConfirm(null);
          }
        }}
      />

      <InviteByEmailDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        siteId={site.id}
        orgId={site.org_id}
      />
    </div>
  );
}
